import { Dialog, type DialogBoxSize, type DialogProps } from "@mdotm/shadcn/legacy/components";
import { Button, type FormSubmitFn, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type NodeOrFn,
	WrapIfStrOrNumOrEmpty,
	adaptAnimatedNodeProvider,
	focusOnMount,
	renderNodeOrFn,
	spawn,
} from "@mdotm/shadcn/legacy/react-extensions";
import type { ReactNode } from "react";

export type YesNoDialogCommon = {
	children?: ReactNode;
	header?: string | ReactNode;
	yesButton?: string | NodeOrFn;
	disableYesButton?: boolean;
	onSubmitAsync?: FormSubmitFn<void>;
	noButton?: string | NodeOrFn<{ onClick(): void }>;
	size?: DialogBoxSize;
};

export type SpawnYesNoDialogParams = YesNoDialogCommon;

export function spawnYesNoDialog(params?: SpawnYesNoDialogParams): Promise<boolean> {
	const { onSubmitAsync, ...forward } = params ?? {};

	return spawn<boolean>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<YesNoDialog
				size="medium"
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(false)}
				onSubmitAsync={async (...p) => {
					await onSubmitAsync?.(...p);
					resolve(true);
				}}
				{...forward}
			/>
		)),
	).promise;
}

export type YesNoDialogProps = YesNoDialogCommon & {
	show: boolean;
	onClose?(): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
};

export function YesNoDialog(props: YesNoDialogProps): React.JSX.Element {
	return (
		<Dialog
			size={props.size ?? "small"}
			show={props.show}
			onAnimationStateChange={props.onAnimationStateChange}
			onClose={props.onClose}
			onSubmitAsync={props.onSubmitAsync}
			header={props.header ?? "Do you want to continue?"}
			footer={
				<div className="flex justify-between">
					<WrapIfStrOrNumOrEmpty
						wrapper={(content) => (
							<Button
								palette="tertiary"
								size="small"
								type="button"
								onClick={() => props.onClose?.()}
								data-qualifier="YesNoDialog/CancelButton"
							>
								{content}
							</Button>
						)}
					>
						{renderNodeOrFn(props.noButton ?? "No", { onClick: () => props.onClose?.() })}
					</WrapIfStrOrNumOrEmpty>
					<WrapIfStrOrNumOrEmpty
						wrapper={(content) => (
							<SubmitButton
								innerRef={focusOnMount}
								disabled={props.disableYesButton}
								palette="primary"
								size="small"
								data-qualifier="YesNoDialog/ConfirmButton"
							>
								{content}
							</SubmitButton>
						)}
					>
						{renderNodeOrFn(props.yesButton ?? "Yes")}
					</WrapIfStrOrNumOrEmpty>
				</div>
			}
		>
			{props.children}
		</Dialog>
	);
}
